import React from 'react';
import type { InventoryStockMovementItemSummaryRow } from '@/renderer/types/inventory';
import { formatReportMoney } from '@/renderer/util/reporting';
import { RefreshCw } from 'lucide-react';

export function StockMovementSummaryTable({
  rows,
  isLoading = false,
}: {
  rows: InventoryStockMovementItemSummaryRow[];
  isLoading?: boolean;
}) {
  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <div className="relative max-h-[32rem] overflow-y-auto">
        {isLoading ? (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-start justify-center bg-white/65 pt-5">
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 shadow-sm">
              <RefreshCw size={13} className="animate-spin" />
              Updating summary
            </span>
          </div>
        ) : null}
        <table
          className={`w-full text-sm transition-opacity ${isLoading ? 'opacity-60' : ''}`}
          aria-busy={isLoading}
        >
          <thead className="sticky top-0 border-b border-gray-100 bg-gray-50">
            <tr className="text-left">
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Item
              </th>
              <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                Qty in
              </th>
              <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                Qty out
              </th>
              <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                Net qty
              </th>
              <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                Value
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.inventory_item_id}
                className="border-t border-gray-100"
              >
                <td className="px-5 py-4">
                  <div className="font-medium text-gray-900">
                    {row.item_name || 'Unknown item'}
                  </div>
                  <div className="text-xs text-gray-500">
                    {row.sku || 'No SKU'}
                  </div>
                </td>
                <td className="px-5 py-4 text-right text-gray-600">
                  {row.quantity_in.toLocaleString(undefined, {
                    maximumFractionDigits: 4,
                  })}
                  {row.unit_label && (
                    <span className="ml-1 text-xs font-normal text-gray-400">
                      {row.unit_label}
                    </span>
                  )}
                </td>
                <td className="px-5 py-4 text-right text-gray-600">
                  {row.quantity_out.toLocaleString(undefined, {
                    maximumFractionDigits: 4,
                  })}
                  {row.unit_label && (
                    <span className="ml-1 text-xs font-normal text-gray-400">
                      {row.unit_label}
                    </span>
                  )}
                </td>
                <td className="px-5 py-4 text-right font-medium text-gray-900">
                  {row.net_quantity.toLocaleString(undefined, {
                    maximumFractionDigits: 4,
                  })}
                  {row.unit_label && (
                    <span className="ml-1 text-xs font-normal text-gray-400">
                      {row.unit_label}
                    </span>
                  )}
                </td>
                <td className="px-5 py-4 text-right text-gray-600">
                  {formatReportMoney(row.total_value)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
